import { showError } from 'helpers/handleError';
import PromiseHandler from 'helpers/promiseHandler';
import { isEmpty, isPlainObject } from 'lodash';
import { ResponderV2Obj } from 'models/responder-v2.model';
import { useEffect, useState, useCallback, useRef } from 'react';
import ResponderV2Service from 'services/ResponderV2Service';

//* GUIDE:
//**************************************
//* Check services of API              *
//* Check parseBodyToRequest function  *
//* Check checkConditionPass function  *
//**************************************

let dataStored = {};

/**
 * @returns {{
 *  data: {"actionType":{"fire_request":"Fire request","forward_call":"Forward call","play_audio":"Play audio","send_voice":"Send voice","send_message":"Send message"},"intents":["ABOUT SELF QUESTION","ABOUT SELF RESPONSE","APOLOGY","APPRECIATION","ARRIVAL TIME","ARRIVAL TIME RESPONSE","COMPLIMENT","COMPLIMENT RESPONSE","CUSTOMER SERVICE","DISSATISFACTION","GOOD NIGHT","GOODBYE","GREETING","GREETING RESPONSE","HOURS OF OPERATION","IDENTITY QUESTION","INFORMATION REQUEST","INFORMATION REQUEST RESPONSE","INSULT","INSULT RESPONSE","INTENT QUESTION","LOCATION REQUEST","LOVE YOU","LOVE YOU RESPONSE","MISS YOU","MISS YOU RESPONSE","NICE MEETING","NICE TALKING","NOT INTERESTED","NOT WELL","OPTIN GENERAL CONSENT","OPTIN REQUEST FOR DEALS","OPTOUT REQUEST","POSITIVE RESPONSE","QUESTION GREETING","QUESTION GREETING RESPONSE","REQUEST TO BE CONTACTED","THANKS","THANKS RESPONSE","CATCH ALL"],"variables":["[Province State Name]","[Vehicle Maker]","[Vehicle Model]","[Vehicle Year]","[Vertical]","[Zip Code]","[Address]","[Web Browser]","[Phone Brand]","[Phone Type]","[Last Name]","[IP Address]","[First Name]","[State Code]","[Sub Aff1]","[Sub Aff2]","[Sub Aff3]","[Sub Aff4]","[Sub Aff5]","[Carrier]","[City]","[Contract]","[Country]","[Birthday]","[Email]","[EVID]","[Gender]","[Link]","[Name]","[OS]","[Phone]","[Source]","[Url]","[Language]","[Device Name]","[Phone Md5]","[Device Class]","[Home Owner]","[Family Status]","[Education Level]","[Job Category]","[Having Children]","[Children Birthday]","[Household Size]","[Dwelling Age]","[Dwelling Type]","[Engagement]","[Creation Date]","[Clicked Count]","[Replied Count]","[Converted Count]","[Blocked Count]","[Campaign Title]","[Job Title]","[Credit Rating]","[Employer]","[Contact Group Name]","[Target Number]","[Adset Name]","[Ethnicity]","[Income]","[Form Name]"],"triggerType":{"listen_call":"Listen whole voice call","answered_call":"Answered call","forward_call":"Forward call","inbound_message":"Inbound message","dtmf":"DTMF during voice call","inbound_call":"Inbound call","click_link":"Click on short link","live_feed":"Live feed"}},
 *  loading: boolean,
 *  refetching: boolean,
 *  error: string | any,
 *  refetch: () => Promise<void>,
 *  refetchWithLoading: () => Promise<void>,
 *  setData: Dispatch<SetStateAction<any[]>>,
 * }}
 */
const useGetConfigResponder = ({ isTrigger = true } = {}) => {
  const promiseHandler = useRef(new PromiseHandler());
  const [data, setData] = useState(dataStored);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [refetching, setRefetching] = useState(false);

  //! Function
  const checkConditionPass = useCallback((response) => {
    //* Check condition of response here to set data
    if (isPlainObject(response?.data)) {
      setData(response?.data);
      dataStored = response?.data;

      const { actionType, triggerType } = dataStored;
      ResponderV2Obj.set('actionType', actionType);
      ResponderV2Obj.set('triggerType', triggerType);
    }
  }, []);

  const fetch = useCallback(() => {
    if (!isTrigger || !isEmpty(dataStored)) {
      return;
    }

    return new Promise(async (resolve, reject) => {
      try {
        const response = await promiseHandler.current.takeLatest(ResponderV2Service.getConfig());
        resolve(response);
      } catch (error) {
        setError(error);
        reject(error);
      }
    });
  }, [isTrigger]);

  //* Refetch implicity (without changing loading state)
  const refetch = useCallback(async () => {
    try {
      setRefetching(true);
      const response = await fetch();
      checkConditionPass(response);
    } catch (error) {
      if (!!error?.isCanceled) {
        return;
      }
      showError(error);
    } finally {
      setRefetching(false);
    }
  }, [fetch, checkConditionPass]);

  //* Refetch with changing loading state
  const refetchWithLoading = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch();
      checkConditionPass(response);
      setLoading(false);
    } catch (error) {
      if (!!error?.isCanceled) {
        return;
      }
      showError(error);
    } finally {
      setLoading(false);
    }
  }, [fetch, checkConditionPass]);

  useEffect(() => {
    let shouldSetData = true;

    const fetchAPI = async () => {
      try {
        setLoading(true);
        const response = await fetch();
        if (shouldSetData) {
          checkConditionPass(response);
          setLoading(false);
        }
      } catch (error) {
        showError(error);
        setLoading(false);
      }
    };

    fetchAPI();

    return () => {
      promiseHandler.current.cancel();
      shouldSetData = false;
    };
  }, [fetch]);

  return { data, loading, error, refetching, refetch, refetchWithLoading, setData };
};

export default useGetConfigResponder;
